import { moveToFulfilled, moveToPending, moveToRejected, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  AllNetworkGroupsLoadFulfilledAction,
  AllNetworkGroupsLoadMoreFulfilledAction,
} from '../../domains/allNetworkGroups/actions.types';
import type { CreateEventFulfilledAction } from '../../domains/createEvent/actions.types';
import type { EditEventFulfilledAction } from '../../domains/editEvent/actions.types';
import type { EventLoadByBroadcastIdFulfilledAction } from '../../domains/eventLoadByBroadcastId/actions.types';
import type { FirstRunSuggestedGroupsLoadFulfilledAction } from '../../domains/firstRunSuggestedGroups/actions.types';
import type {
  GroupCoverImageDeleteFulfilledAction,
  GroupCoverImageUploadFulfilledAction,
} from '../../domains/groupCoverImages/actions.types';
import type { GroupHeaderLoadAction } from '../../domains/groupHeaderLoad/actions.types';
import type { GroupRelatedGroupsLoadFulfilledAction } from '../../domains/groupRelatedGroupsLoad/actions.types';
import type { LiveEventLoadByGroupIdFulfilledAction } from '../../domains/liveEventLoadByGroupId/actions.types';
import type { LiveEventLoadByNetworkFulfilledAction } from '../../domains/liveEventsLoadByNetwork/actions.types';
import type {
  MyGroupsLoadFulfilledAction,
  MyGroupsLoadMoreFulfilledAction,
} from '../../domains/myGroups/actions.types';
import type {
  OfficialGroupsLoadFulfilledAction,
  OfficialGroupsLoadMoreFulfilledAction,
} from '../../domains/officialGroups/actions.types';
import type {
  RecommendedGroupsLoadFulfilledAction,
  RecommendedGroupsLoadMoreFulfilledAction,
} from '../../domains/recommendedGroups/actions.types';
import type { SuggestedActionsLoadFulfilledAction } from '../../domains/suggestedActions/actions.types';
import type {
  SuggestedGroupsLoadFulfilledAction,
  SuggestedGroupsLoadMoreFulfilledAction,
} from '../../domains/suggestedGroups/actions.types';

import type { GroupCoverImages } from './types';

export type GroupCoverImageAction =
  | CreateEventFulfilledAction
  | EditEventFulfilledAction
  | EventLoadByBroadcastIdFulfilledAction
  | GroupHeaderLoadAction
  | GroupCoverImageUploadFulfilledAction
  | GroupCoverImageDeleteFulfilledAction
  | GroupRelatedGroupsLoadFulfilledAction
  | LiveEventLoadByGroupIdFulfilledAction
  | LiveEventLoadByNetworkFulfilledAction
  | RecommendedGroupsLoadFulfilledAction
  | RecommendedGroupsLoadMoreFulfilledAction
  | SuggestedActionsLoadFulfilledAction
  | FirstRunSuggestedGroupsLoadFulfilledAction
  | SuggestedGroupsLoadFulfilledAction
  | SuggestedGroupsLoadMoreFulfilledAction
  | AllNetworkGroupsLoadFulfilledAction
  | AllNetworkGroupsLoadMoreFulfilledAction
  | MyGroupsLoadFulfilledAction
  | MyGroupsLoadMoreFulfilledAction
  | OfficialGroupsLoadFulfilledAction
  | OfficialGroupsLoadMoreFulfilledAction;

const initialState: GroupCoverImages = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<GroupCoverImages, GroupCoverImageAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_COVER_IMAGE.UPLOAD_FULFILLED':
      return update(state, action.payload.groupId, { backgroundUrl: action.payload.url, hasDefaultCoverImage: false });
    case 'GROUP_COVER_IMAGE.DELETE_FULFILLED':
      return update(state, action.payload.groupId, {
        backgroundUrl: action.payload.coverImageUrlTemplate,
        hasDefaultCoverImage: true,
      });
    case 'CREATE_EVENT.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'EDIT_EVENT.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'EVENT.LOAD_BY_BROADCAST_ID_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'FRE_SUGGESTED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'GROUP_HEADER.LOAD_PENDING':
      return moveToPending(state, [action.payload.groupId]);
    case 'GROUP_HEADER.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.groupId], action.payload.error);
    case 'GROUP_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'ALL_NETWORK_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'GROUP_RELATED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'RECOMMENDED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'RECOMMENDED_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'SUGGESTED_ACTION.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'SUGGESTED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'SUGGESTED_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'LIVE_EVENT.LOAD_BY_GROUP_ID_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'LIVE_EVENT.LOAD_BY_NETWORK_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'MY_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'MY_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    case 'OFFICIAL_GROUPS.LOAD_FULFILLED':
    case 'OFFICIAL_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupCoverImages);
    default:
      return state;
  }
};

export default reducer;
